/**
 * RentalsPromo.jsx
 * Created by Destin Frasier on 9/04/20.
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useOffsetSize} from '../../../hooks';
import * as Icons from '../icons/Icons';
import { ExternalLink } from '../Link';
import FauxImage from '../FauxImage';

export default function RentalsPromo({
    ariaLabel,
    copy,
    buttonCopy,
    href,
    isGearPromo,
    isInternalLink,
    isRvPromo,
    showArrow,
    className,
}) {

    const maxCopyLength = 85;
    const targetRef = useRef();
    const { height } = useOffsetSize(targetRef);

    const rvRentalPromoImage = "rv-rental-banner-background";
    const gearRentalPromoImage = "gear-rental-banner-background";

    // Select a random background image
    const randomPromoImage = useRef(
        [rvRentalPromoImage, gearRentalPromoImage][Math.floor(Math.random() * 2)]
    );
    
    let promoImageInventoryType = []
    if (isGearPromo){
        promoImageInventoryType = gearRentalPromoImage
    } else if (isRvPromo){
        promoImageInventoryType = rvRentalPromoImage
    }

    const arrow  = (
        <span className="rec-rentals-promo-icon-wrap">
            <Icons.IconArrowForward />
        </span>
    );

    const promoButtonCopy = () => {
        return (
            buttonCopy.length >= 1 ? (
                <span className="rec-rentals-promo-button-text">
                    &nbsp;{buttonCopy}
                    {arrow}
                </span>
            ) : (
                <span className="rec-rentals-promo-button-text">
                    {showArrow && (
                        arrow
                    )}
                </span>
            )
        );
    }

    const promoImage = () => {
        return (
            isGearPromo || isRvPromo ? (
                <FauxImage
                    src={`/shared/img/global/rentals/${promoImageInventoryType}.jpg`}
                    altText="Example"
                    style={{ minHeight: `${height}px` }}
                />
            ) : (
                <FauxImage
                    src={`/shared/img/global/rentals/${randomPromoImage.current}.jpg`}
                    altText="Example"
                    style={{ minHeight: `${height}px` }}
                />
            )
        );
    }

    return (
        <div
            ref={targetRef}
            className={cx(className, 'rec-rentals-promo-wrap')}
        >
            {/* Define whether the promo directs users to a Rec splash page or external link. */}
            {isInternalLink ? (
                <a
                    href={href}
                    aria-label={ariaLabel}
                    target="_blank"
                    rel="noopener noreferrer">
                    <div className={cx('rec-rentals-promo-inner-wrap')}>
                        <p className={cx({
                            'rec-rentals-promo-reduced-font-size' : copy.length >= maxCopyLength
                        })}>
                            {copy}
                            {promoButtonCopy()}
                        </p>
                    </div>
                    {promoImage()}
                </a>
            ) : (
                <ExternalLink
                    url={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={ariaLabel}>
                    <div className={cx('rec-rentals-promo-inner-wrap')}>
                        <p className={cx({
                            'rec-rentals-promo-reduced-font-size' : copy.length >= maxCopyLength
                        })}>
                            {copy}
                            {promoButtonCopy()}
                        </p>
                    </div>
                    {promoImage()}
                </ExternalLink>
            )}
        </div>
    );
}

RentalsPromo.propTypes = {
    /** Aria-Label for the promo */
    ariaLabel: PropTypes.string,

    /** Bold copy displayed directly before the arrow */
    buttonCopy: PropTypes.string,

    /** Added to existing classes */
    className: PropTypes.string,

    /** Copy for the promo */
    copy: PropTypes.string,

    /** Url for the promo */
    href: PropTypes.string,

    // Display the Gear Rental background image */
    isGearPromo: PropTypes.bool,

    /** Does this promo direct users to a Rec splash page, or external site */
    isInternalLink: PropTypes.bool,

    // Display the RV Rental background image */
    isRvPromo: PropTypes.bool,

    // Show or hide the arrow directly after the promo copy */
    showArrow: PropTypes.bool,
};

RentalsPromo.defaultProps = {
    ariaLabel: 'Gear and RV rental options.',
    buttonCopy: '',
    className: undefined,
    copy: undefined,
    href: '/rentals',
    isGearPromo: undefined,
    isInternalLink: true,
    isRvPromo: undefined,
    showArrow: true,
};
